import React, { useCallback, useEffect, useState } from "react";
import { FiSearch, FiUserPlus, FiUsers, FiX } from "react-icons/fi";
import { apiService } from "../../../api/apiService";
import s from "./DodatkoweOsoby.module.css";

/**
 * [OBSZAR D 2026-09-11] DOPISANIE OSÓB DO ZAKŁADANEGO ZGŁOSZENIA.
 *
 * Zgłoszenie właściciela, dosłownie: „dodaj do każdego zgłoszenia tworzonego też przez system dodania
 * osób (wyszukiwanie z listy userów z aplikacji, żeby można było dodać więcej osób niż zgłaszający —
 * każdy z nich dostanie to zgłoszenie w sekcji /moje-zgloszenia i będzie mógł sprawdzać jego realizację)”.
 *
 * Kontrolka jest niesterowana treścią zgłoszenia — trzyma wyłącznie listę wybranych kont i oddaje ją
 * w górę. Wysyłką i odsiewaniem (autor, konta wygaszone, konta bez adresu) zajmuje się serwer:
 * tutaj nie ma ani jednej reguły dostępowej, bo taka reguła w przeglądarce byłaby tylko sugestią.
 *
 * ── [BH-3 2026-09-19] NAPISY SĄ PARAMETREM, MECHANIKA NIE ───────────────────────────────────
 * Wniosek o dostęp do zasobów potrzebuje DOKŁADNIE tej samej kontrolki („wyszukiwanie po
 * pracownikach z aplikacji, jako dodanie nawet kilku osób"), ale mówi o czym innym: tam lista
 * znaczy „dla kogo ma być nadany dostęp", a nie „kto będzie śledził sprawę". Druga kopia
 * komponentu rozjechałaby się przy pierwszej poprawce wyszukiwarki — przechwycenie Entera niżej
 * powstało po wypadku i musi obowiązywać w KAŻDYM użyciu. Dlatego zmienne są wyłącznie napisy,
 * a wartości domyślne zostawiają stare użycia bez zmiany.
 */
const DodatkoweOsoby = ({
    wybrani,
    onZmiana,
    disabled = false,
    tytul = "Dodatkowe osoby w zgłoszeniu",
    wyjasnienie = "Te osoby zobaczą zgłoszenie w swojej sekcji „Moje zgłoszenia” i będą śledzić jego realizację. "
        + "Zamknąć sprawę może dalej tylko zgłaszający.",
    placeholder = "Wyszukaj osobę po imieniu, nazwisku lub adresie…",
    tekstPustegoStanu = "Nikogo nie dopisano — zgłoszenie zobaczysz tylko Ty.",
    testId = "dodatkowe-osoby",
}) => {
    const [fraza, setFraza] = useState("");
    const [wyniki, setWyniki] = useState([]);
    const [szuka, setSzuka] = useState(false);

    useEffect(() => {
        const term = fraza.trim();
        if (term.length < 2) {
            setWyniki([]);
            return undefined;
        }
        let zywy = true;
        setSzuka(true);
        const t = setTimeout(() => {
            apiService
                .searchPeople(term, 12)
                .then((r) => {
                    if (zywy) setWyniki(Array.isArray(r?.data) ? r.data : []);
                })
                .catch(() => {
                    if (zywy) setWyniki([]);
                })
                .finally(() => {
                    if (zywy) setSzuka(false);
                });
        }, 350);
        return () => {
            zywy = false;
            clearTimeout(t);
        };
    }, [fraza]);

    const dodaj = useCallback(
        (u) => {
            if (wybrani.some((w) => w.id === u.id)) return;
            onZmiana([
                ...wybrani,
                {
                    id: u.id,
                    nazwa: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email,
                    email: u.email,
                },
            ]);
            setFraza("");
            setWyniki([]);
        },
        [wybrani, onZmiana]
    );

    const usun = useCallback(
        (id) => onZmiana(wybrani.filter((w) => w.id !== id)),
        [wybrani, onZmiana]
    );

    return (
        <div className={s.blok} data-testid={testId}>
            <div className={s.naglowek}>
                <span className={s.tytul}>
                    <FiUsers size={12} /> {tytul}
                </span>
                {wybrani.length > 0 && <span className={s.licznik}>{wybrani.length}</span>}
            </div>

            {/* Zdanie mówi o SKUTKU, nie o kontrolce. Bez niego „dodatkowe osoby” brzmi jak DW,
                a to jest coś innego: te osoby dostają sprawę na własną listę i śledzą jej realizację. */}
            <p className={s.wyjasnienie}>{wyjasnienie}</p>

            {wybrani.length > 0 && (
                <div className={s.pigulki}>
                    {wybrani.map((w) => (
                        <span key={w.id} className={s.pigulka} title={w.email}>
                            <span className={s.nazwa}>{w.nazwa}</span>
                            <button
                                type="button"
                                className={s.usun}
                                onClick={() => usun(w.id)}
                                disabled={disabled}
                                title="Usuń z listy"
                            >
                                <FiX size={11} />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            <div className={s.szukajka}>
                <FiSearch className={s.szukajkaIkona} size={13} />
                <input
                    type="text"
                    className={s.szukajkaInput}
                    value={fraza}
                    onChange={(e) => setFraza(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    /* 🔴 To pole siedzi w formularzu, który wysyła Enterem. Bez tego przechwycenia
                       naciśnięcie Entera w wyszukiwarce osób ZAKŁADAŁOBY zgłoszenie — w trakcie
                       dobierania odbiorców, czyli w najgorszym możliwym momencie. */
                    onKeyDown={(e) => {
                        if (e.key === "Enter") e.preventDefault();
                    }}
                />
            </div>

            {fraza.trim().length >= 2 && (
                <div className={s.wyniki}>
                    {szuka && <div className={s.info}>Szukam…</div>}
                    {!szuka && wyniki.length === 0 && (
                        <div className={s.info}>Brak kont dla „{fraza.trim()}”.</div>
                    )}
                    {wyniki.map((u) => {
                        const juz = wybrani.some((w) => w.id === u.id);
                        return (
                            <button
                                key={u.id}
                                type="button"
                                className={s.wynik}
                                onClick={() => dodaj(u)}
                                disabled={disabled || juz}
                            >
                                <span className={s.wynikNazwa}>
                                    {`${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email}
                                    {juz && <span className={s.juz}>już na liście</span>}
                                </span>
                                <span className={s.wynikMeta}>
                                    {u.email}
                                    {Array.isArray(u.departments) && u.departments.length > 0
                                        ? ` · ${u.departments[0]}`
                                        : ""}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {wybrani.length === 0 && fraza.trim().length < 2 && (
                <span className={s.pustyStan}>
                    <FiUserPlus size={12} /> {tekstPustegoStanu}
                </span>
            )}
        </div>
    );
};

export default DodatkoweOsoby;
